import React, { useEffect, useMemo, useState } from "react";
import ApiURL from "../controllers/Api";
import CompanyLayout from "./CompanyLayout";

const rowsPerPage = 10;

const formatLpa = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})} LPA`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

const getListPreview = (items = [], fallback = "-") => {
  const cleanItems = (items || []).filter(Boolean);
  if (!cleanItems.length) return fallback;
  return cleanItems.length === 1 ? cleanItems[0] : `${cleanItems[0]}...`;
};

export default function CompanyRecruiterPerformance() {
  const [summary, setSummary] = useState({
    total_recruiters: 0,
    active_recruiters: 0,
    total_assignments: 0,
    active_pipeline: 0,
    total_hires: 0,
    total_followups: 0,
    total_hired_amount: 0,
  });
  const [recruiters, setRecruiters] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${ApiURL}crm/company/get_recruiter_dashboard.php`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.success === false) {
          throw new Error(result.message || "Failed to load recruiter performance");
        }
        setSummary(
          result?.data?.summary || {
            total_recruiters: 0,
            active_recruiters: 0,
            total_assignments: 0,
            active_pipeline: 0,
            total_hires: 0,
            total_followups: 0,
            total_hired_amount: 0,
          }
        );
        setRecruiters(result?.data?.recruiters || []);
      } catch (error) {
        console.error("Error loading recruiter performance:", error);
      }
    };

    loadData();
  }, []);

  const filteredRecruiters = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return recruiters;
    return recruiters.filter((recruiter) =>
      [
        recruiter.name,
        recruiter.email,
        recruiter.phone,
        recruiter.personnel_id,
        recruiter.designation,
        ...(recruiter.hired_companies || []),
        ...(recruiter.hired_requirements || []),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [recruiters, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredRecruiters.length / rowsPerPage));
  const paginatedRecruiters = filteredRecruiters.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <CompanyLayout
      title="Recruiter Performance"
      description="Track recruiter productivity, assigned resumes, hires, followups, and selected companies from one clean page."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Total Recruiters</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.total_recruiters || 0}</div>
            <div className="mt-2 text-xs text-slate-500">{summary.active_recruiters || 0} active right now</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Assigned Resumes</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.total_assignments || 0}</div>
            <div className="mt-2 text-xs text-slate-500">Distinct resumes owned by recruiters</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Active Pipeline</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.active_pipeline || 0}</div>
            <div className="mt-2 text-xs text-slate-500">Distinct resumes still in progress</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Total Hires</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.total_hires || 0}</div>
            <div className="mt-2 text-xs text-slate-500">Distinct resumes successfully hired</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Hired Amount</div>
            <div className="mt-2 text-2xl font-bold text-slate-900">{formatLpa(summary.total_hired_amount || 0)}</div>
            <div className="mt-2 text-xs text-slate-500">{summary.total_followups || 0} followups logged</div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Recruiter Performance Table</h3>
              <p className="mt-1 text-sm text-slate-500">
                Review one recruiter at a time with proper pagination and distinct resume counts.
              </p>
            </div>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search recruiter, company, requirement..."
              className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm md:max-w-sm"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Recruiter</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Contact</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Assigned</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Active</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Hires</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Followups</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Selected Companies</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Hired Value</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedRecruiters.map((recruiter) => (
                  <tr key={recruiter.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{recruiter.name}</div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {recruiter.designation || "Recruiter"}{recruiter.personnel_id ? ` • ${recruiter.personnel_id}` : ""}
                      </div>
                      <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[10px] font-medium ${recruiter.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {recruiter.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>{recruiter.email || "-"}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{recruiter.phone || "-"}</div>
                    </td>
                    <td className="px-3 py-3 font-medium text-slate-900">{recruiter.total_assignments || 0}</td>
                    <td className="px-3 py-3 font-medium text-amber-700">{recruiter.active_pipeline || 0}</td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-emerald-700">{recruiter.total_hires || 0}</div>
                      {recruiter.hired_requirements?.length ? (
                        <div className="mt-1 text-[11px] text-slate-500" title={recruiter.hired_requirements.join(", ")}>
                          {getListPreview(recruiter.hired_requirements)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>{recruiter.total_followups || 0} total</div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {recruiter.pending_followups || 0} pending • {recruiter.completed_followups || 0} done
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-600" title={recruiter.hired_companies?.join(", ") || "-"}>
                      {getListPreview(recruiter.hired_companies)}
                    </td>
                    <td className="px-3 py-3 text-slate-900 font-medium">{formatLpa(recruiter.total_hired_amount || 0)}</td>
                    <td className="px-3 py-3 text-slate-500">{formatDate(recruiter.last_activity)}</td>
                  </tr>
                ))}
                {paginatedRecruiters.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                      No recruiter performance records found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-500">
              Showing {paginatedRecruiters.length} of {filteredRecruiters.length} recruiters
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-slate-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </CompanyLayout>
  );
}
